import { useState, useEffect, useContext, ReactElement } from "react";
import { ISite } from "@river/interfaces";
import { customerService } from "../../services";
import {
  PrimaryButton,
  RiverSpinner,
  useNotification,
} from "@river/common-ui";
import EditIcon from "@mui/icons-material/Edit";
import { CustomerContext } from "../customer";
import { SidebarContext } from "../sidebar";
import { PageHeader } from "../page-header";
import { SubHeader } from "../sub-header";
import { Box } from "@mui/material";
import { RiverDataGrid } from "../river-data-grid";
import sharedStyles from "../shared-styles/shared-styles.module.scss";
import { getCustomerSidebarContent } from "../customer/sidebar-content";
import { Column, RowsChangeData } from "react-data-grid";
import { AddSiteDialog } from "../add-site-dialog";
import styles from "./customer-sites.module.scss";

export const CustomerSites = () => {
  const notify = useNotification();
  const { customer } = useContext(CustomerContext)!;
  const [sites, setSites] = useState<ISite[]>([]);
  const [editSite, setEditSite] = useState<ISite | undefined>();
  const [addSiteDialogOpened, setAddSiteDialogOpened] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const sidebarContext = useContext(SidebarContext);

  useEffect(() => {
    sidebarContext?.setSidebarContent(getCustomerSidebarContent(customer!));
    loadSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSites = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const result = await customerService.getSites(customer!._id);
      setSites(result);
    } catch (message) {
      notify.error({ message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClicked = (site: ISite) => {
    setEditSite(site)
    setAddSiteDialogOpened(true)
  }

  const columns: Column<ISite>[] = [
    {
      key: "site",
      name: "Site",
    },
    {
      key: "edit",
      name: "Edit",
      width: 80,
      formatter: ({row}) => {
        return (<EditIcon className={styles.formatterIcon} onClick={()=>handleEditClicked(row)}/>);
      },
    },
  ];

  const onRowsChange = (rows: ISite[], data: RowsChangeData<any, any>) => {
    setSites(rows);
  };

  const openAddSiteDialog = (): void => setAddSiteDialogOpened(true);
  const closeAddSiteDialog = (): void => setAddSiteDialogOpened(false);


  const renderAddSiteButton = (): ReactElement => (
    <PrimaryButton onClick={openAddSiteDialog} text="ADD SITE" />
  );
  return (
    <>
      <PageHeader title="Sites" />
      <SubHeader subtitle={customer?.description!} />
      <Box className={sharedStyles.primaryButtons}>{renderAddSiteButton()}</Box>
      <Box className={sharedStyles.viewContent}>
        <RiverDataGrid
          className={sharedStyles.dataGrid}
          columns={columns}
          rows={sites}
          onRowsChange={onRowsChange}
        />
      </Box>
      <RiverSpinner show={isLoading} />

      <AddSiteDialog
        open={addSiteDialogOpened}
        customer={customer!}
        siteToEdit={editSite}
        onClose={(success) => {
          closeAddSiteDialog();
          if (success) loadSites();
          setEditSite(undefined)
        }}
      />
    </>
  );
};
